import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFixedMenuPosition } from '../useFixedMenuPosition';

/**
 * ★ The Fan rated table's QTY WIDE x HIGH control (Anna, 2026-10-08 / 2026-10-13).
 *
 * The handler rates one fan model against several arrays and reports which ones
 * it is valid for; this is where the user says which of them to carry into the
 * selection. Two things are asked of the list, and the second is why this is not
 * a `<select>` any more:
 *
 *  1. The arrays that FIT the section's airway come first, the ones that do not
 *     follow (`orderWideHighOptions`).
 *  2. An array that does not fit is RED — but only the number that does not fit.
 *     `4 x 2` needing more width than the section has reddens the `4` alone;
 *     needing more height reddens the `2` alone; the `x` between them is always
 *     black.
 *
 * A native `<option>` renders as a single run of text in one colour — there is
 * no way to colour half of it, in the open list or on the closed control — so
 * the control is the same custom one the Data Bar already uses (PaginatedSelect,
 * §7.7b): a styled face plus a `ul.options` list portaled to `<body>` and
 * anchored in viewport space (`useFixedMenuPosition`), which is also what keeps
 * the list from being clipped by the rated table's own scroll box. The OPEN
 * list is painted to look like the popup the `<select>` used to drop (`MENU_BG`
 * and below), so what changed in this column is the colour of a number, not the
 * control it is in.
 *
 * The face reproduces the legacy `select` chrome exactly (common.css:29571 —
 * 26px tall, 0/20/0/5 padding, the two-gradient caret, the #ddd underline), so
 * the column looks the way it did while the rating dialog is open; the pieces
 * that a `<div>` does NOT get for free from that rule are `letter-spacing`
 * (`.rate-list-inline` sets -0.25em and a form control does not inherit it) and
 * `text-align` (`.number-col` right-aligns the cell, and a form control does not
 * inherit that either), both stated below.
 */

/** One entry of the rated row's QTY WIDE x HIGH list. */
export interface WideHighOption {
  value: string;
  label: string;
  qty_wide?: number;
  qty_high?: number;
  /** does this array's REQUIRED AW WIDTH fit inside the SECTION's airway?
   *  Undefined where it cannot be known (the header popup has no section, and a
   *  vendor row without `min_aw_*` has no required airway) — treated as fitting,
   *  i.e. never coloured and never pushed down the list. */
  widthFits?: boolean;
  /** …and the same for the REQUIRED AW HEIGHT. */
  heightFits?: boolean;
}

/** The red the rated table already uses for an over-airway REQUIRED AW value
 *  (common.css:18582 `.fan_rated_item .fan_field[data-name=aw_width_height]
 *  span.warning`, mirrored by CoilSelector's `AW_WARNING`). */
const UNFIT_COLOR = '#dc362e';

/** The normal colour of a rated-table value — the legacy sheet's body text
 *  colour, measured on the `<select>` this replaces (computed `rgb(33, 33, 33)`)
 *  and the same `#212121` the rest of the port hardcodes from common.css.
 *
 * It is stated on every number, the fitting ones included, so that a red number
 * can never bleed into its neighbour: the `x` and the dimension that does fit
 * keep their own colour whatever the other one is doing. */
const FIT_COLOR = '#212121';

/**
 * ★ Top and bottom margin of the control, so the pick sits on the SAME line as
 * its row's model name (Anna, 2026-10-12).
 *
 * It is the only control in a table of plain text. It is 26px tall where a rated
 * row's text line is 13px, so the cell it is in was 13px taller than every other
 * cell in the row: the row stretched from the standard 39px to 51px, and because
 * the control centres its own text in that box, the pick rendered ~6px BELOW the
 * model name and the REQUIRED AW / RPI / SPEED numbers either side of it — on
 * every row.
 *
 * Collapsing the control's inline-block margin box back to that 13px line fixes
 * both at once: 14px off the pair leaves it its full size and lets the row go
 * back to 39px.
 *
 * The 14 splits 6/8 rather than 7/7 because this is a `div`, not the `select` it
 * replaces: an inline-block with `overflow: hidden` is baseline-aligned by its
 * bottom MARGIN edge instead of by the baseline of the text in it, which sat the
 * whole control 1px high. Measured on the rated table (`qty-face-align.mjs`):
 * 6/8 puts the pick's text centre exactly on the centre of the REQUIRED AW text
 * beside it, where 7/7 is 1px above it.
 *
 * Padding on the CELL would do the same arithmetic, but the cell is also what
 * renders the plain-text fallback for a stored rated fan (no options, no
 * control), and that text needs the normal 12px — the margin lives with the
 * control so it can only ever apply where one is.
 */
const SELECT_MARGIN_TOP = -6;
const SELECT_MARGIN_BOTTOM = -8;

/** The legacy `select` chrome (common.css:29571), on a div. */
const FACE_STYLE: React.CSSProperties = {
  display: 'inline-block',
  // the width handed in IS the rendered width — `.number_field` carries ~25px
  // of padding and a border that content-box would add on top of it.
  boxSizing: 'border-box',
  height: 26,
  // 26px minus the 1px underline, so the text sits where the `<select>` put it.
  lineHeight: '25px',
  padding: '0 20px 0 5px',
  fontFamily: 'Roboto',
  fontSize: 13,
  // `.rate-list-inline` is `letter-spacing: -0.25em` (the inline-block gap hack);
  // a `<select>` does not inherit that and neither may this.
  letterSpacing: 'normal',
  // …nor the `.number-col` right-align: the pick reads from the left edge of the
  // control, as it did in the `<select>`.
  textAlign: 'left',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
  outline: 'none',
  userSelect: 'none',
  backgroundColor: 'transparent',
  borderBottom: '1px solid #ddd',
  backgroundImage:
    'linear-gradient(130deg, #9f9f9f 3.5px, transparent 4px), linear-gradient(-130deg, #9f9f9f 3.5px, transparent 4px)',
  backgroundPosition: '100% 11px, calc(100% - 10px) 11px',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '10px 20px',
  marginTop: SELECT_MARGIN_TOP,
  marginBottom: SELECT_MARGIN_BOTTOM,
};

/** The menu is portaled out to `<body>`, i.e. out of the dialog's stacking
 *  context, so it has to out-rank the DIALOG, not just the table it opens over.
 *  The header Independent Rating host is `.popup-wrapper.component-selector-popup`,
 *  `z-index: 10000` while `.active` (rating_mode.scss:581-591) — 10000 would tie
 *  it and lose on tree order — and the selects inside the dialog go up to 9999
 *  (`.ka-series-select__list`, :2523). 10010 clears both. */
const MENU_Z_INDEX = 10010;

/** The two numbers of an array, as rendered. Prefers the parsed qty fields and
 *  falls back to splitting the label, so an option shape without them (or one
 *  that is not a pair at all) still renders something sensible. */
const wideHighParts = (option: WideHighOption): [string, string | null] => {
  if (option.qty_wide != null && option.qty_high != null) {
    return [String(option.qty_wide), String(option.qty_high)];
  }
  const m = /^\s*(.+?)\s*x\s*(.+?)\s*$/i.exec(option.label ?? '');
  return m ? [m[1], m[2]] : [option.label ?? '', null];
};

/** `<wide> x <high>`, each number in its own colour, the separator always black —
 *  except on the highlighted row of the open list, which is white throughout, as
 *  the native popup painted it (see `HIGHLIGHT_COLOR`). */
export const WideHighLabel = ({
  option,
  highlighted = false,
}: {
  option: WideHighOption;
  highlighted?: boolean;
}) => {
  const [wide, high] = wideHighParts(option);
  const fit = highlighted ? HIGHLIGHT_COLOR : FIT_COLOR;
  const unfit = highlighted ? HIGHLIGHT_COLOR : UNFIT_COLOR;
  if (high == null) return <span style={{ color: fit }}>{wide}</span>;
  return (
    <>
      <span style={{ color: option.widthFits === false ? unfit : fit }}>{wide}</span>
      <span style={{ color: fit }}>{' x '}</span>
      <span style={{ color: option.heightFits === false ? unfit : fit }}>{high}</span>
    </>
  );
};

/**
 * ★ The same pair as plain TEXT, for a row that has no option list (Anna,
 * 2026-10-15).
 *
 * A rated row only carries `wide_high_options` while the vendor response that
 * produced it is still in hand. Reopen the dialog on a SELECTED fan and the row
 * is rebuilt from the stored AHUFan (`buildSelectedRow`): one historical array,
 * nothing to choose between, so the column renders text instead of the control.
 *
 * That text was black — the array that did not fit the section when it was
 * rated lost its red the moment the dialog was closed and reopened, while the
 * REQUIRED AW W x H cell two columns over kept showing the same over-airway
 * number in red. Same row, same verdict, two answers.
 *
 * So the text reads the verdict off exactly the fields that cell does
 * (`aw_width_warning` / `aw_height_warning`, stamped by `buildSelectedRow` and
 * re-stamped when the open GET lands), through the same `WideHighLabel` the
 * control uses — one dimension red at a time, the `x` always black. A row with
 * neither flag (a vendor that reports no `min_aw_*`, or the header-mode popup,
 * which has no section to compare against) renders black throughout, as before.
 */
export const WideHighText = ({ row }: { row: Record<string, any> }) => {
  if (row.qty_wide == null || row.qty_high == null) return null;
  return (
    <WideHighLabel
      option={{
        value: `${row.qty_wide}x${row.qty_high}`,
        label: `${row.qty_wide} x ${row.qty_high}`,
        qty_wide: row.qty_wide,
        qty_high: row.qty_high,
        // `!flag` and not the `known` gate `orderWideHighOptions` uses: that
        // gate exists to keep an unknown verdict out of the fit-first ORDER,
        // and there is no order here — a row is red exactly when the cell
        // beside it is.
        widthFits: !row.aw_width_warning,
        heightFits: !row.aw_height_warning,
      }}
    />
  );
};

/**
 * ★ The open list wears the chrome the native `<select>` popup had (Anna,
 * 2026-10-14).
 *
 * `.custom-select-with-pagination ul.options` (common.css:32603) is the Data
 * Bar's own list — white, a #ccc hairline, a pale #eee highlight — and on a
 * white rated table that reads as no list at all: the pick the user is choosing
 * between is a grey-on-white row over grey-on-white rows. The native popup this
 * control replaced was its own object on the page, so the values below are the
 * ones measured off the screenshot of it (Chrome's menu chrome): a #e6e6e6
 * sheet inside a #767676 border, 26px rows, and the row under the cursor
 * solid #1967d2 with WHITE text.
 *
 * White text on the highlight is the native rule and not an oversight of the
 * red: Chrome paints the highlighted row's text white whatever colour the
 * option asked for — in the "before" screenshot the option carrying an explicit
 * `#212121` renders white under the cursor. So the fit/unfit red says its piece
 * on every other row, and the one being pointed at reads as a selection.
 */
const MENU_BG = '#e6e6e6';
const MENU_BORDER = '#767676';
const OPTION_HEIGHT = 26;
const HIGHLIGHT_BG = '#1967d2';
const HIGHLIGHT_COLOR = '#ffffff';

/** One row of the open list — what `.custom-select-with-pagination ul.options
 *  li.option-li` gave (ellipsis, no wrap, no stray `li` margin) at the native
 *  popup's own 26px row rather than that rule's `!important` 4px padding. */
const OPTION_STYLE: React.CSSProperties = {
  display: 'block',
  height: OPTION_HEIGHT,
  lineHeight: `${OPTION_HEIGHT}px`,
  // 6px to the left of the first number is where the native popup put it
  // (measured: its text started 7px inside a 1px border).
  padding: '0 6px',
  margin: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
};

/** Does this array fit the section's airway in BOTH dimensions? Unknown counts
 *  as fitting — see `WideHighOption.widthFits`. */
const optionFits = (o: WideHighOption) => o.widthFits !== false && o.heightFits !== false;

/**
 * ★ Fit-first ordering, and the per-dimension flag the colours are drawn from.
 *
 * The arrays a fan model rated for are not equally usable: the required airway
 * grows with the array (`calc_required_aw`), so the wider/taller layouts can need
 * more airway than the SECTION has — which is the comparison the REQUIRED AW W x H
 * column already renders red per row. This lifts that verdict into the list: the
 * arrays that fit the section come first, the ones that do not follow and are
 * coloured, both in the open list and on the closed control.
 *
 * The verdict is read off each array's OWN mapped variant row (`aw_width_warning`
 * / `aw_height_warning`, set in `runRate` against the open-time section AW) rather
 * than recomputed here, so the column and the control can never disagree.
 *
 * `row` is the fallback for the array the row itself carries: a rating of ONE
 * explicit array attaches the option but no variant payload for it (annexair
 * `handle_call.py:149` passes `{}`), and that array's warnings are on the row.
 *
 * `filter` is stable, so the backend's ascending grid order (`attach_wide_high`
 * sorts the layouts) survives inside each group — the list stays 1x1, 1x2, 2x1, …
 * within "fits" and again within "does not fit".
 */
export const orderWideHighOptions = (
  options: Array<Record<string, any>>,
  variantRows: Record<string, Record<string, any>>,
  row: Record<string, any>
): WideHighOption[] => {
  const flagged: WideHighOption[] = options.map((o) => {
    const value = String(o.value);
    const rated =
      variantRows[value] ??
      (value === String(row.wide_high_selected ?? '') ? row : undefined);
    // No required airway on the rated row (a vendor that reports no `min_aw_*`,
    // or the header popup, which has no section to compare against) = no verdict.
    const known = rated != null && rated.aw_width != null && rated.aw_height != null;
    return {
      ...(o as WideHighOption),
      widthFits: known ? !rated.aw_width_warning : undefined,
      heightFits: known ? !rated.aw_height_warning : undefined,
    };
  });
  return [...flagged.filter(optionFits), ...flagged.filter((o) => !optionFits(o))];
};

interface FanWideHighSelectProps {
  options: WideHighOption[];
  /** the option `value` currently on the row. */
  value: string;
  onChange: (value: string) => void;
  /** px width of the control — the measured column heading (see `measureQtyHead`
   *  in FanSelector); null keeps the pre-measurement full-column width. */
  width: number | null;
}

export const FanWideHighSelect = ({ options, value, onChange, width }: FanWideHighSelectProps) => {
  const [open, setOpen] = useState(false);
  // The highlighted row of the open list; -1 = none. The native popup kept one
  // at all times — the current pick while the mouse is away from the list, the
  // row under the cursor once it is over it, and the arrow cursor on the
  // keyboard — so this is all three.
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const menuPos = useFixedMenuPosition(open, rootRef, listRef, [options.length]);

  // Keep the keyboard cursor in view. The list is a 200px scroller
  // (`.custom-select-with-pagination ul.options`, common.css:32603) and a `--`
  // grid can be 24 arrays, so arrowing past ~8 of them would otherwise move the
  // highlight below the fold and Enter would commit an array nobody can see —
  // which is the one thing the `<select>` did for free.
  useEffect(() => {
    if (!open || active < 0) return;
    const li = listRef.current?.children[active] as HTMLElement | undefined;
    li?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  /** The row the list opens on: the current pick, as the native popup did. */
  const selectedIndex = options.findIndex((o) => o.value === value);

  // Per-instance outside-click close, as in PaginatedSelect: the menu is
  // portaled out of the root, so "inside" means either box — without the second
  // test a click on the list's own padding would read as an outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const pick = (next: string) => {
    setOpen(false);
    setActive(-1);
    if (next !== value) onChange(next);
  };

  /** What a `<select>` gave for free: open/close and move on the keyboard. The
   *  cursor only commits on Enter — arrowing through the list the way a closed
   *  `<select>` does would swap the whole rated row (and, for a curve vendor,
   *  POST for a curve) on every keystroke. */
  const onKeyDown = (e: React.KeyboardEvent) => {
    const move = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
    if (!open) {
      if (move || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setActive(Math.max(0, selectedIndex));
        setOpen(true);
      }
      return;
    }
    if (move) {
      e.preventDefault();
      setActive((i) => Math.min(options.length - 1, Math.max(0, (i < 0 ? 0 : i) + move)));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (options[active]) pick(options[active].value);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
      setActive(-1);
    }
  };

  /**
   * The menu is portaled to `<body>`, and it has to be — see PaginatedSelect's
   * note: `#ahu-board-content` is a stacking context, so a z-index inside the
   * board is sealed in with it. The `display: contents` wrapper exists only so
   * the legacy stylesheet still reaches the list: the scroller it is
   * (`max-height: 200px; overflow-y: auto`) comes from the DESCENDANT selector
   * `.custom-select-with-pagination ul.options` (common.css:32603), which a bare
   * portaled `<ul>` would fall out of. Its colours do NOT: the sheet, the border
   * and the highlight are stated here, as the native popup wore them (see
   * `MENU_BG`). The rows carry no `option-li` class for the same reason — that
   * rule's padding is `!important`, so a row could not be the 26px the native
   * one was; everything it gave is restated in `OPTION_STYLE`.
   *
   * ⚠ React portals keep bubbling through the REACT TREE, not the DOM one, so
   * an option's click would reach the rated ROW's own `chooseFan` handler — and
   * it has to be stopped there (see the option's `onClick`). The row is already
   * the chosen one by then: the click on the FACE that opened the list is the
   * one that chose it, and that one still bubbles, exactly as the `<select>`'s
   * did.
   */
  const menu = createPortal(
    <div className="custom-select-with-pagination" style={{ display: 'contents' }}>
      <ul
        ref={listRef}
        className="options fan-wide-high-options"
        role="listbox"
        style={
          menuPos
            ? {
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                zIndex: MENU_Z_INDEX,
                textAlign: 'left',
                letterSpacing: 'normal',
                background: MENU_BG,
                border: `1px solid ${MENU_BORDER}`,
                padding: 0,
                margin: 0,
                listStyle: 'none',
                // portaled to <body>, so it inherits nothing from the dialog:
                // the list reads in the control's own font, as the popup did.
                fontFamily: 'Roboto',
                fontSize: 13,
                ...(menuPos.off
                  ? { visibility: 'hidden' as const, pointerEvents: 'none' as const }
                  : null),
              }
            : // One layout pass before the hook has measured — fixed already,
              // just not anchored yet; useLayoutEffect fills it in before paint.
              { position: 'fixed', visibility: 'hidden' }
        }
      >
        {options.map((o, i) => (
          <li
            key={o.value}
            data-value={o.value}
            role="option"
            aria-selected={o.value === value}
            style={i === active ? { ...OPTION_STYLE, backgroundColor: HIGHLIGHT_BG } : OPTION_STYLE}
            // The highlight follows the cursor, as the native popup's did, and
            // goes back to the current pick when the cursor leaves the list.
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(selectedIndex)}
            // ⚠ The row's own click handler must NOT see this. Both run in one
            // dispatch with no render in between, so the row's `chooseFan`
            // would re-choose this row from the PRE-swap rows its closure still
            // holds and put the array the user just left back on the curve
            // (and, for a curve vendor whose fetch is still in flight, risk
            // caching that curve onto the new row). `chooseWideHigh` re-chooses
            // the row with the swapped rows itself.
            onClick={(e) => {
              e.stopPropagation();
              pick(o.value);
            }}
          >
            <WideHighLabel option={o} highlighted={i === active} />
          </li>
        ))}
      </ul>
    </div>,
    document.body
  );

  return (
    <>
      <div
        ref={rootRef}
        data-name="wide_high_face"
        data-value={value}
        className="fan_field number_field"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        tabIndex={0}
        style={{ ...FACE_STYLE, width: width != null ? width : 'calc(100% - 10px)' }}
        onClick={() => {
          setActive(open ? -1 : Math.max(0, selectedIndex));
          setOpen((o) => !o);
        }}
        onKeyDown={onKeyDown}
      >
        {selected ? <WideHighLabel option={selected} /> : ''}
      </div>
      {open && menu}
    </>
  );
};
